import { useQueryClient } from "@tanstack/react-query"
import { DOCUMENT_EVENT_NAMES, type RealtimeMessage } from "@zplanem/responses"
import { useCallback } from "react"
import { usePerUserChannelEvents } from "./use-per-user-channel-event"

/**
 * Mounted app-wide: when a document grant/revoke flips the signed-in user's
 * access (ZPL-259), refresh that property's document queries (the property/
 * project lists + the grant-candidates panel). Delivered on the per-user channel
 * because access is per-user; the affected queries are property-scoped, so we
 * invalidate every `["properties", propertyId, …, "documents", …]` key by
 * predicate on the payload's propertyId.
 */
export function useDocumentAccessRealtime(userId: string | undefined): void {
	const queryClient = useQueryClient()
	const handler = useCallback(
		(data: unknown) => {
			const msg = data as RealtimeMessage
			if (msg.event !== "document.access.changed") return
			const { propertyId } = msg.payload
			void queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "properties" &&
					query.queryKey[1] === propertyId &&
					query.queryKey.includes("documents"),
			})
		},
		[queryClient],
	)
	usePerUserChannelEvents(userId, DOCUMENT_EVENT_NAMES, handler)
}
